import { OfflineViewState, SceneConfig as OctreeSceneConfig, View } from "@novorender/api";
import type { Webviewer } from "lib";
import { createContext, Dispatch } from "react";

import type { ObjectDataAccess } from "dataAccess";
import { AppObjectDB } from "utils/appObjectDb";
import { EventEmitter } from "utils/EventEmitter";
import { SelectionSet } from "utils/selectionSet";

/**
 * Procore PDF/drawing sheet ids for the current scene.
 *
 * `objectIds` is filled by the scene-wide searches started during initialization.
 */
export type PdfObjectDiscovery = {
    status: "idle" | "pending" | "ready" | "failed";
    /** The scene the ids belong to; `null` until discovery starts. */
    sceneId: string | null;
    objectIds: SelectionSet;
};

/**
 * The discovered ids, or `undefined` in every state where they are not the whole answer:
 * `idle` and `pending` have collected nothing yet, and `failed` never will. Consumers of a
 * half-filled set would recognize some sheets and not others, so they take `undefined` as
 * "no set" and fall back to reading the object.
 *
 * @see resolvePdfObjectIds in `hooks/useObjectTreeHidden` for the tree-accounting variant,
 * which treats `failed` as an empty set — it cannot read an object to make up the
 * difference, and must not block on ids it will never have.
 */
export function readyPdfObjectIds(discovery: PdfObjectDiscovery | undefined): SelectionSet | undefined {
    return discovery?.status === "ready" ? discovery.objectIds : undefined;
}

export const initialState = {
    view: undefined as undefined | View,
    scene: undefined as undefined | OctreeSceneConfig,
    /**
     * This is effectively deprecated. For Procore use {@link initialState.dataAccess}.
     * dataAccess would also work with standalone, but since it's not clear if standalone will get bim-index support,
     * migrating db -> dataAccess might not bring any benefits.
     */
    db: undefined as undefined | AppObjectDB,
    /**
     * Engine-agnostic object data access, and where object metadata and hierarchy
     * should come from: a consumer neither knows nor cares which backend answers, or
     * whether the answer came from the network. The implementation is chosen once at
     * startup (see `useHandleInit`) and never swaps underneath a consumer.
     */
    dataAccess: undefined as undefined | ObjectDataAccess,
    canvas: null as null | HTMLCanvasElement,
    size: { width: 0, height: 0 },
    offlineWorkerState: undefined as undefined | OfflineViewState,
    availableObjectIds: undefined as undefined | SelectionSet,
    forbiddenObjectIds: undefined as undefined | Uint32Array,
    pdfObjectDiscovery: {
        status: "idle",
        sceneId: null,
        objectIds: SelectionSet.EMPTY,
    } as PdfObjectDiscovery,
    webviewer: undefined as undefined | Webviewer,
    // Internal EventEmitter for app mode - always available
    internalEventEmitter: new EventEmitter(),
};

enum ActionTypes {
    Set,
    Update,
    UpdatePdfObjectDiscovery,
}

function update(updates: Partial<State>) {
    return {
        type: ActionTypes.Update as const,
        updates,
    };
}

function set(state: State) {
    return {
        type: ActionTypes.Set as const,
        state,
    };
}

/**
 * Ids are merged rather than replaced so a later update cannot drop findings
 * already recorded for the same scene.
 */
function updatePdfObjectDiscovery(update: {
    sceneId: string | null;
    status?: PdfObjectDiscovery["status"];
    addObjectIds?: Iterable<number>;
}) {
    return {
        type: ActionTypes.UpdatePdfObjectDiscovery as const,
        update,
    };
}

export const actions = { set, update, updatePdfObjectDiscovery };

type State = typeof initialState;
export type HydratedState = Pick<
    { [K in keyof State]: NonNullable<State[K]> },
    "view" | "scene" | "db" | "dataAccess" | "canvas" | "offlineWorkerState" | "internalEventEmitter"
> &
    Omit<State, "view" | "scene" | "db" | "dataAccess" | "canvas" | "offlineWorkerState" | "internalEventEmitter">;

export type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type ContextType = { state: State | HydratedState; dispatch: Dispatch<Actions> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Context = createContext<ContextType>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Set: {
            return action.state;
        }
        case ActionTypes.Update: {
            return {
                ...state,
                ...action.updates,
            };
        }
        case ActionTypes.UpdatePdfObjectDiscovery: {
            const { sceneId, status, addObjectIds } = action.update;
            const current = state.pdfObjectDiscovery;
            const isSameScene = current.sceneId === sceneId;
            const objectIds = isSameScene ? current.objectIds : SelectionSet.EMPTY;
            return {
                ...state,
                pdfObjectDiscovery: {
                    sceneId,
                    status: status ?? (isSameScene ? current.status : "pending"),
                    objectIds: addObjectIds ? objectIds.withAdded(addObjectIds) : objectIds,
                },
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
